import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Kanban, Building2, Brain,
  Clock, Globe, Shield, ChevronRight, ChevronLeft, X, Play, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const TOUR_STEPS = [
  {
    id: 'dashboard', route: '/dashboard', icon: LayoutDashboard, title: 'Dashboard',
    tagline: 'Deine taegliche Kommandozentrale',
    description: 'KPI-Karten zeigen Gesamt-Mitglieder, Konversionsraten, aktive Kurse und überfällige Nachfassungen auf einen Blick. Pipeline-Diagramme zeigen Engpaesse. Der Wochentrend verrät, ob Marketing funktioniert.',
    realEstateWhy: 'Das Dashboard sagt dir in 3 Sekunden, was heute Aufmerksamkeit braucht.',
    color: '#3B82F6',
  },
  {
    id: 'leads', route: '/leads', icon: Users, title: 'Mitglieder-Datenbank',
    tagline: 'Jeder Kontakt, jedes Detail',
    description: 'Vollstaendige Kontaktprofile mit Name, Telefon, E-Mail, Budget, Zeitrahmen und Interesse. Sofortige Suche, Filterung nach Stufe oder Quelle. Import per CSV in Sekunden. Klicke auf ein Mitglied für die komplette Interaktions-Timeline.',
    realEstateWhy: 'Ohne zentrale Datenquelle gehen Deals verloren. Das hier ist diese Quelle.',
    color: '#8B5CF6',
  },
  {
    id: 'pipeline', route: '/pipeline', icon: Kanban, title: 'Pipeline-Board',
    tagline: 'Deals durch den Trichter ziehen',
    description: 'Visuelles Kanban-Board mit 7 konfigurierbaren Stufen: Neu, Kontaktiert, Qualifiziert, Besuch, Verhandlung, Gewonnen, Verloren. Drag & Drop zwischen Stufen. Budget, nächstes Follow-up und Zuweisung auf jeder Karte.',
    realEstateWhy: 'Wenn ein Trainer 15 Mitglieder organisiert sieht statt eines chaotischen Spreadsheets, geht er nie zurück.',
    color: '#F59E0B',
  },
  {
    id: 'properties', route: '/properties', icon: Building2, title: 'Kurse',
    tagline: 'Dein Angebot, übersichtlich organisiert',
    description: 'Kurs-Karten mit Fotos, Preisen und Verfügbarkeitsstatus. Filtere nach Typ und Status. Jeder Kurs zeigt interessierte Mitglieder — sieh wer sich wofür interessiert.',
    realEstateWhy: 'Wenn ein Mitglied fragt "Was bietet ihr ab 50€ an?", antwortest du in 5 Sekunden.',
    color: '#10B981',
  },
  {
    id: 'ai', route: '/leads', icon: Brain, title: 'KI-Funktionen',
    tagline: 'KI sagt dir, wen du zuerst anrufen sollst',
    description: 'Ein-Klick KI-Bewertung benotet jedes Mitglied von A bis D basierend auf Budget, Engagement und Zeitrahmen. KI generiert auch professionelle Kursbeschreibungen. Admins wählen das Modell in den Einstellungen.',
    realEstateWhy: 'Ein Trainer mit 50 Mitgliedern kann nicht alle heute anrufen. KI-Scoring priorisiert die vielversprechendsten.',
    color: '#EC4899',
  },
  {
    id: 'followups', route: '/follow-ups', icon: Clock, title: 'Nachfass-System',
    tagline: 'Nie wieder einen Rueckruf verpassen',
    description: 'Plane Nachfassungen pro Mitglied mit Typ (Anruf/E-Mail/Treffen/Besuch) und Faelligkeitsdatum. Überfällig-Warnungen zeigen was zu spaet ist. Ein-Klick "Erledigt" Funktion.',
    realEstateWhy: '80% der Abschluesse erfordern 5+ Nachfassungen. Dieses System macht dein Team zu ausdauernden Closern.',
    color: '#EF4444',
  },
  {
    id: 'capture', route: '/settings', icon: Globe, title: 'Erfassungs-Formular',
    tagline: 'Website-Besucher werden Pipeline-Leads',
    description: 'Ein öffentliches, einbettbares Web-Formular das direkt in deine Pipeline eingespeist wird. Keine API-Keys, keine Integrationen — einfach den Link kopieren und einbetten.',
    realEstateWhy: 'Ein Interessent fuellt um 23 Uhr dein Formular aus. Um 8 Uhr ist es schon einem Trainer zugewiesen.',
    color: '#06B6D4',
  },
  {
    id: 'rbac', route: '/team', icon: Shield, title: 'Team & Zugriffskontrolle',
    tagline: 'Jeder sieht genau das, was er braucht',
    description: 'Drei eingebaute Rollen: Admin sieht alles, Manager sieht das Team, Trainer sieht nur seine Mitglieder. Rollenwechsel mit einem Klick.',
    realEstateWhy: 'Wenn du Trainer #6 einstellst, soll er nicht die Mitgliederliste von Trainer #1 sehen. RBAC macht dies zum Team-Tool.',
    color: '#8B5CF6',
  },
];

export default function DemoTour({ active, onClose }) {
  const [step, setStep] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const current = TOUR_STEPS[step];

  useEffect(() => {
    if (active) {
      setTimeout(() => setVisible(true), 50);
    } else {
      setVisible(false);
    }
  }, [active]);

  const goToStep = useCallback((newStep) => {
    if (newStep < 0 || newStep >= TOUR_STEPS.length) return;
    const target = TOUR_STEPS[newStep];
    setIsNavigating(true);
    if (location.pathname !== target.route) {
      navigate(target.route);
    }
    setTimeout(() => { setStep(newStep); setIsNavigating(false); }, 300);
  }, [navigate, location.pathname]);

  const handleNext = () => { if (step < TOUR_STEPS.length - 1) goToStep(step + 1); else onClose(); };
  const handlePrev = () => { if (step > 0) goToStep(step - 1); };

  useEffect(() => {
    if (active && location.pathname !== current.route) navigate(current.route);
  }, [active]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!active) return;
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  if (!active) return null;

  const Icon = current.icon;
  const progress = ((step + 1) / TOUR_STEPS.length) * 100;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[100] bg-black/40 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Tour Card */}
      <div
        className={`fixed z-[101] bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] sm:w-[680px] max-w-[calc(100vw-2rem)] transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        data-testid="demo-tour-card"
      >
        <div className="bg-background border border-border rounded-xl shadow-2xl overflow-hidden">
          {/* Progress bar */}
          <div className="h-1 bg-muted">
            <div className="h-full rounded-full transition-all duration-400" style={{ width: `${progress}%`, background: current.color }} />
          </div>

          <div className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: `${current.color}15` }}>
                  <Icon className="w-5 h-5" style={{ color: current.color }} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold tracking-tight">{current.title}</h3>
                    <span className="text-[10px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{step + 1} of {TOUR_STEPS.length}</span>
                  </div>
                  <p className="text-sm font-medium" style={{ color: current.color }}>{current.tagline}</p>
                </div>
              </div>
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1" data-testid="demo-tour-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="space-y-3 fade-in">
              <p className="text-sm text-foreground/80 leading-relaxed">{current.description}</p>
              <div className="rounded-lg p-3 border" style={{ background: `${current.color}08`, borderColor: `${current.color}20` }}>
                <div className="flex items-start gap-2">
                  <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: current.color }} />
                  <p className="text-xs leading-relaxed" style={{ color: current.color }}>
                    <span className="font-semibold">Warum das wichtig ist: </span>{current.realEstateWhy}
                  </p>
                </div>
              </div>
            </div>

            {/* Dots */}
            <div className="flex items-center justify-center gap-1.5 mt-5 mb-4">
              {TOUR_STEPS.map((s, i) => (
                <button key={s.id} onClick={() => goToStep(i)} data-testid={`demo-step-${i}`}>
                  <div className={`rounded-full transition-all duration-300 ${i === step ? 'w-6 h-2' : 'w-2 h-2 opacity-30 hover:opacity-60'}`}
                    style={{ background: i === step ? current.color : 'hsl(var(--muted-foreground))' }} />
                </button>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={handlePrev} disabled={step === 0 || isNavigating} className="gap-1.5 text-muted-foreground" data-testid="demo-prev-btn">
                <ChevronLeft className="w-4 h-4" /> Zurück
              </Button>
              <Button size="sm" onClick={onClose} variant="ghost" className="text-xs text-muted-foreground" data-testid="demo-skip-btn">Tour beenden</Button>
              <Button size="sm" onClick={handleNext} disabled={isNavigating} style={{ background: current.color }} data-testid="demo-next-btn" className="gap-1.5">
                {step === TOUR_STEPS.length - 1 ? 'Tour abschliessen' : 'Weiter'} {step < TOUR_STEPS.length - 1 && <ChevronRight className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export function DemoModeBanner({ onStart }) {
  return (
    <div className="mb-5">
      <div className="relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-r from-primary/5 via-primary/[0.03] to-transparent p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Play className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">Neu hier? Schnelle Tour starten</p>
              <p className="text-xs text-muted-foreground">8 Funktionen erklaert in unter 2 Minuten</p>
            </div>
          </div>
          <Button size="sm" onClick={onStart} className="gap-2 shadow-sm" data-testid="start-demo-tour-btn">
            <Play className="w-3.5 h-3.5" /> Demo starten
          </Button>
        </div>
      </div>
    </div>
  );
}
